'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Star, ThumbsUp, MessageSquare, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Rating {
  id: string;
  user_id: string;
  rating: number;
  review_title: string | null;
  review_body: string | null;
  is_verified_investor: boolean;
  helpful_count: number;
  created_at: string;
  user_profiles?: { full_name: string; email: string } | null;
}

interface RatingSummary {
  avg: number;
  total: number;
  distribution: Record<number, number>;
}

interface CampaignRatingSectionProps {
  campaignId: string;
  campaignName: string;
}

// ─── Star Display ─────────────────────────────────────────────────────────────

function StarRow({
  value,
  interactive = false,
  size = 16,
  onChange,
}: {
  value: number;
  interactive?: boolean;
  size?: number;
  onChange?: (v: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  const display = interactive && hovered > 0 ? hovered : value;

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={!interactive}
          onClick={() => onChange?.(s)}
          onMouseEnter={() => interactive && setHovered(s)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={interactive ? 'cursor-pointer focus:outline-none' : 'cursor-default'}
        >
          <Star
            size={size}
            className={
              s <= display
                ? 'text-warning fill-warning' :'text-muted-foreground/30'
            }
          />
        </button>
      ))}
    </div>
  );
}

// ─── Rating Bar ───────────────────────────────────────────────────────────────

function RatingBar({ star, count, total }: { star: number; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-3 text-right">{star}</span>
      <Star size={10} className="text-warning fill-warning flex-shrink-0" />
      <div className="flex-1 h-1.5 bg-surface-4 rounded-full overflow-hidden">
        <div className="h-full bg-warning rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-muted-foreground w-5 text-right">{count}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignRatingSection({ campaignId, campaignName }: CampaignRatingSectionProps) {
  const { user } = useAuth();
  const supabase = createClient();

  const [ratings, setRatings] = useState<Rating[]>([]);
  const [summary, setSummary] = useState<RatingSummary>({ avg: 0, total: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formRating, setFormRating] = useState(0);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [userExistingRating, setUserExistingRating] = useState<Rating | null>(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('campaign_ratings')
        .select('*, user_profiles(full_name, email)')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;

      const rows: Rating[] = data ?? [];
      setRatings(rows);

      // Compute summary
      const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let sum = 0;
      rows.forEach((r) => {
        dist[r.rating] = (dist[r.rating] ?? 0) + 1;
        sum += r.rating;
      });
      setSummary({
        avg: rows.length > 0 ? Math.round((sum / rows.length) * 10) / 10 : 0,
        total: rows.length,
        distribution: dist,
      });

      // Check if current user already rated
      if (user) {
        const mine = rows.find((r) => r.user_id === user.id) ?? null;
        setUserExistingRating(mine);
        if (mine) {
          setFormRating(mine.rating);
          setFormTitle(mine.review_title ?? '');
          setFormBody(mine.review_body ?? '');
        }
      }
    } catch (e: any) {
      setError(e.message ?? 'Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, [campaignId, user]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (formRating === 0) {
      setError('Please select a star rating.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (userExistingRating) {
        // Update
        const { error: upErr } = await supabase
          .from('campaign_ratings')
          .update({
            rating: formRating,
            review_title: formTitle || null,
            review_body: formBody || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userExistingRating.id);
        if (upErr) throw upErr;
        setSuccessMsg('Your review has been updated.');
      } else {
        // Insert
        const { error: insErr } = await supabase
          .from('campaign_ratings')
          .insert({
            campaign_id: campaignId,
            user_id: user.id,
            rating: formRating,
            review_title: formTitle || null,
            review_body: formBody || null,
          });
        if (insErr) throw insErr;
        setSuccessMsg('Thank you for your review!');
      }
      setShowForm(false);
      await fetchRatings();
    } catch (e: any) {
      setError(e.message ?? 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!userExistingRating) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: delErr } = await supabase
        .from('campaign_ratings')
        .delete()
        .eq('id', userExistingRating.id);
      if (delErr) throw delErr;
      setUserExistingRating(null);
      setFormRating(0);
      setFormTitle('');
      setFormBody('');
      setShowForm(false);
      setSuccessMsg('Your review has been removed.');
      await fetchRatings();
    } catch (e: any) {
      setError(e.message ?? 'Failed to delete review');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const displayName = (r: Rating) => {
    const name = r.user_profiles?.full_name;
    if (name && name.trim()) return name;
    const email = r.user_profiles?.email ?? '';
    return email ? email.split('@')[0] : 'Anonymous';
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="glass-card rounded-xl p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <MessageSquare size={15} className="text-primary" />
          Ratings &amp; Reviews
        </h2>
        {user && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="btn-secondary text-xs gap-1.5"
          >
            <Star size={12} />
            {userExistingRating ? 'Edit Your Review' : 'Write a Review'}
          </button>
        )}
      </div>

      {/* Feedback banners */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <AlertCircle size={13} className="flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={12} /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2 text-xs text-positive bg-positive/10 border border-positive/20 rounded-lg px-3 py-2">
          <CheckCircle size={13} className="flex-shrink-0" />
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-auto"><X size={12} /></button>
        </div>
      )}

      {/* Review Form */}
      {showForm && user && (
        <form onSubmit={handleSubmit} className="bg-surface-3 rounded-xl p-4 space-y-4 border border-border">
          <p className="text-xs font-semibold text-foreground">
            {userExistingRating ? 'Update your review' : `Review: ${campaignName}`}
          </p>

          {/* Star picker */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Your Rating *</label>
            <StarRow value={formRating} interactive size={22} onChange={setFormRating} />
          </div>

          {/* Title */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Review Title</label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Summarise your experience"
              maxLength={120}
              className="w-full bg-surface-4 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Body */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Review</label>
            <textarea
              value={formBody}
              onChange={(e) => setFormBody(e.target.value)}
              placeholder="Share your thoughts on this campaign…"
              rows={3}
              maxLength={1000}
              className="w-full bg-surface-4 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 justify-end flex-wrap">
            {userExistingRating && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                Remove review
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-ghost text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || formRating === 0}
              className="btn-primary text-xs gap-1.5"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
              {userExistingRating ? 'Update' : 'Submit Review'}
            </button>
          </div>
        </form>
      )}

      {/* Not logged in prompt */}
      {!user && (
        <p className="text-xs text-muted-foreground bg-surface-3 rounded-lg px-3 py-2">
          <a href="/login" className="text-primary hover:underline">Sign in</a> to leave a review.
        </p>
      )}

      {/* Summary */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="animate-spin text-muted-foreground" />
        </div>
      ) : summary.total === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">No reviews yet. Be the first to review this campaign.</p>
      ) : (
        <>
          {/* Aggregate */}
          <div className="flex items-start gap-6 p-4 bg-surface-3 rounded-xl">
            <div className="text-center flex-shrink-0">
              <p className="text-4xl font-bold text-foreground font-mono-data">{summary.avg.toFixed(1)}</p>
              <StarRow value={Math.round(summary.avg)} size={13} />
              <p className="text-xs text-muted-foreground mt-1">{summary.total} review{summary.total !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex-1 space-y-1.5">
              {[5, 4, 3, 2, 1].map((s) => (
                <RatingBar key={s} star={s} count={summary.distribution[s] ?? 0} total={summary.total} />
              ))}
            </div>
          </div>

          {/* Review list */}
          <div className="space-y-4">
            {ratings.map((r) => (
              <div key={r.id} className="border-t border-border pt-4 space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-foreground">{displayName(r)}</span>
                      {r.is_verified_investor && (
                        <span className="flex items-center gap-0.5 text-xs text-positive">
                          <CheckCircle size={10} /> Verified Investor
                        </span>
                      )}
                    </div>
                    <StarRow value={r.rating} size={12} />
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{formatDate(r.created_at)}</span>
                </div>
                {r.review_title && (
                  <p className="text-sm font-medium text-foreground">{r.review_title}</p>
                )}
                {r.review_body && (
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.review_body}</p>
                )}
                {r.helpful_count > 0 && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ThumbsUp size={11} /> {r.helpful_count} found this helpful
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
